"use client";

import { Database, Lock, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const dataCards = [{
  icon: Database,
  title: "Data Collection",
  description: "We collect only essential information needed to provide our services, with full transparency about what we gather."
}, {
  icon: Shield,
  title: "Data Usage",
  description: "Your data is used exclusively for service delivery and improvement, never sold to third parties."
}, {
  icon: Lock,
  title: "Data Protection",
  description: "Industry-leading encryption and security measures safeguard your information at every step."
}];
export default function HowWeHandleYourData() {
  const {
    ref: section1Ref,
    isVisible: section1Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section2Ref,
    isVisible: section2Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: section3Ref,
    isVisible: section3Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const sectionRefs = [section1Ref, section2Ref, section3Ref];
  const sectionVisibility = [section1Visible, section2Visible, section3Visible];
  return <section className="relative bg-background-dark py-16 sm:py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
      backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(255, 255, 255, 0.03) 10px,
            rgba(255, 255, 255, 0.03) 20px
          )`
    }} />
      
      <div className="container relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground-dark mb-4">
            How We Handle Your Data
          </h2>
          <p className="text-muted-foreground-dark text-lg max-w-2xl mx-auto">
            Your privacy and security are our top priorities. Here's how we protect your information.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {dataCards.map((card, i) => {
          const Icon = card.icon;
          const ref = sectionRefs[i];
          const isVisible = sectionVisibility[i];
          return <div key={i} data-index={i} ref={ref} className="group" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: `all var(--duration-slow) var(--ease-out-back) ${i * 100}ms`
          }}>
                <Card className="h-full bg-primary-light-bg border-border hover:shadow-strong hover:-translate-y-1 hover:scale-[1.02] transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] relative">
                  {}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-accent origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] rounded-b-lg" />
                  
                  <CardHeader className="text-center pb-4">
                    <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-[var(--duration-medium)]">
                      <Icon className="w-8 h-8 text-primary stroke-[1.5]" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground">
                      {card.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="text-center pb-6">
                    <CardDescription className="text-muted-foreground leading-relaxed">
                      {card.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}